import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { OMenuItem } from 'lib/omnibar/types';
import { ConfluenceAPI, PageContent } from 'lib/confluence';
import { RaulMenu, Button, ContainerLoader, Icon, raulUtils } from 'lib/raul';
import { Header } from 'lib/shared';
import { APP } from 'lib/strings';
import { setMetaTags } from 'lib/utils';
import { BASE_URL } from 'lib/config';

import ListView from './ListView';
import DetailView from './DetailView';

const Article = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [childPages, setChildPages] = useState<OMenuItem[]>([]);
  const [pageContent, setPageContent] = useState<PageContent | undefined>();
  const { articleId } = useParams<{ articleId: string }>();
  const [includeToc, setIncludeToc] = useState<boolean>(false);
  const [pageElement, setPageElement] = useState<HTMLElement>(
    document.createElement('div')
  );

  // Load Article Data
  useEffect(() => {
    (async () => {
      setChildPages([]);
      setPageContent(undefined);

      const api = new ConfluenceAPI();

      const children = await api.getNavigationItems(articleId);

      if (children.length > 1) {
        // pop off homepage from list
        children.shift();
        setChildPages(children);
      }

      const content = await api.getPageContent(articleId);

      if (!content) return;

      setPageContent(content);
      setIsLoading(false);
      setMetaTags(content.title, content.excerpt);
    })();
  }, [articleId]);

  useEffect(() => {
    const article = document.createElement('div');

    if (pageContent?.body) {
      article.innerHTML = pageContent?.body;
    }
    setPageElement(article);
  }, [pageContent]);

  // decide is ToC is needed
  useEffect(() => {
    const hasTocMacro = pageElement.querySelector('.toc-macro');

    setIncludeToc(!!hasTocMacro);
  }, [pageElement]);

  if (!pageContent) return <ContainerLoader />;

  return (
    <>
      <Header
        breadcrumbs={pageContent.breadcrumbs}
        className={`r-hide-print ${
          childPages.length ? '' : 'r-border-b r-border-gray-light'
        }`}
        primaryText={pageContent.title}
      >
        <>
          {!childPages.length && (
            <>
              {includeToc && (
                <Button
                  className="r-mr-4"
                  handleClick={raulUtils.handleOpenAside('contents')}
                  size="small"
                  variant="primary"
                >
                  <Icon icon="list-bullets-3" />
                </Button>
              )}
              <RaulMenu
                id="article-action-items"
                items={[
                  {
                    icon: 'data-download-8',
                    text: APP.DOWNLOAD_PDF,
                    url: `${BASE_URL}/pdf/article/${articleId}?filename=${pageContent?.title}.pdf`,
                  },
                ]}
                textSize="sm"
              >
                <Button
                  className="r-mr-4"
                  icon="overflow"
                  size="small"
                  variant="control"
                />
              </RaulMenu>
            </>
          )}
        </>
      </Header>
      {childPages?.length ? (
        <ListView childPages={childPages} isLoading={isLoading} />
      ) : (
        <DetailView
          includeToc={includeToc}
          pageContent={pageContent}
          pageElement={pageElement}
        />
      )}
    </>
  );
};

export default Article;
